import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Handshake, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnershipCard = ({ item, onContacted }) => (
  <div className="bg-white rounded-xl shadow-sm p-5 space-y-2" data-testid={`partnership-card-${item.id}`}>
    <div className="flex flex-wrap justify-between gap-2">
      <div>
        <div className="font-semibold text-gray-900">{item.company_name}</div>
        <div className="text-sm text-gray-600">{item.contact_name}</div>
      </div>
      <div className="text-xs text-gray-500">{new Date(item.created_at).toLocaleString('pt-PT')}</div>
    </div>
    <div className="text-sm flex flex-wrap gap-4">
      <a href={`mailto:${item.email}`} className="text-blue-600 hover:underline">{item.email}</a>
      {item.phone && <a href={`tel:${item.phone}`} className="text-blue-600 hover:underline">{item.phone}</a>}
    </div>
    <p className="text-sm text-gray-700 whitespace-pre-line">{item.message}</p>
    <div className="flex items-center justify-between pt-2">
      <span className={`text-xs ${item.email_sent ? 'text-green-600' : 'text-amber-600'}`}>
        {item.email_sent ? 'Email enviado para a caixa de parcerias' : 'Email não enviado (ver só aqui)'}
      </span>
      <button
        onClick={() => !item.contacted && onContacted(item.id)}
        className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${item.contacted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
        data-testid={`partnership-contacted-${item.id}`}
      >
        {item.contacted && <Check className="w-3 h-3" />}
        {item.contacted ? 'Contactado' : 'Marcar contactado'}
      </button>
    </div>
  </div>
);

export const AdminPartnerships = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/partnerships`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setItems(res.data.partnerships || []))
      .catch(() => toast.error('Erro ao carregar pedidos de parceria'))
      .finally(() => setLoading(false));
  }, [token]);

  const markContacted = async (id) => {
    try {
      await axios.patch(`${API}/admin/partnerships/${id}/contacted`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setItems(prev => prev.map(i => (i.id === id ? { ...i, contacted: true } : i)));
    } catch (err) {
      toast.error('Não foi possível atualizar');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-partnerships">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Handshake className="w-6 h-6 text-tuggo-mint" /> Pedidos de Parceria
      </h2>
      {loading ? (
        <div className="py-12 text-center text-gray-500">A carregar...</div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-gray-500" data-testid="admin-partnerships-empty">Ainda não há pedidos de parceria</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(item => <PartnershipCard key={item.id} item={item} onContacted={markContacted} />)}
        </div>
      )}
    </div>
  );
};
